import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { CampaignOption } from '../types';
import { CampaignStoreGroup, campaignStoreGroups, LIVE_CAMPAIGN_DAYS, matchCampaigns } from '../logic';
import { buttonGroup, plainButton, pressable, srOnly } from '../controls';

interface Props {
  open: boolean;
  onClose: () => void;
  loadCampaigns: () => Promise<CampaignOption[]>;
  /** The search the dialog opens with, such as "araluna" from "Add Araluna campaigns". */
  initialFilter: string;
  initialSelected: string[];
  onSave: (campaignIds: string[]) => Promise<void>;
}

// Why a listed campaign is not live, in words. Live campaigns carry no note.
function notLiveNote(campaign: CampaignOption): string {
  if (campaign.status === 'PAUSED') return 'Paused';
  if (campaign.status === 'REMOVED') return 'Removed';
  return `No spend in ${LIVE_CAMPAIGN_DAYS} days`;
}

const count = (n: number | undefined) => n != null && <Box component="span" sx={{ ml: '5px', color: '#64748B', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{n}</Box>;

export default function CampaignPicker({ open, onClose, loadCampaigns, initialFilter, initialSelected, onSave }: Props) {
  const [campaigns, setCampaigns] = useState<CampaignOption[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected));
  // The Selected view lists what was ticked when it opened, so unticking a row there leaves it in
  // place to tick again instead of making it vanish under the pointer.
  const [pinned, setPinned] = useState<Set<string> | null>(null);
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = () => {
    setLoadError('');
    loadCampaigns().then(setCampaigns, (error: Error) => setLoadError(error.message));
  };
  // The group loads asynchronously after this component first mounts, so re-seed the
  // checkboxes (and reset the filter) each time the dialog opens — otherwise an actor
  // with an existing group opens to all-unchecked and a Save would wipe their group.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set(initialSelected)); setPinned(null); setFilter(initialFilter); setSaveError('');
    if (!campaigns) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groups = useMemo(() => {
    const scoped = (campaigns ?? []).filter(campaign => !pinned || pinned.has(campaign.campaignId));
    return campaignStoreGroups(matchCampaigns(scoped, filter));
  }, [campaigns, pinned, filter]);
  const dirty = selected.size !== initialSelected.length || initialSelected.some(id => !selected.has(id));
  const toggle = (ids: string[], on: boolean) => setSelected(current => {
    const next = new Set(current);
    ids.forEach(id => (on ? next.add(id) : next.delete(id)));
    return next;
  });
  const save = async () => {
    setSaving(true); setSaveError('');
    try { await onSave([...selected]); onClose(); } catch (error: any) { setSaveError(error.message); } finally { setSaving(false); }
  };
  const emptyNote = filter.trim() ? `No campaigns match "${filter.trim()}".` : pinned ? 'No campaigns selected yet.' : `No campaigns spent in the last ${LIVE_CAMPAIGN_DAYS} days.`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ pb: 0.5, fontSize: '1.0625rem', fontWeight: 700 }}>My campaigns</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ color: '#475569', fontSize: '0.8125rem' }}>Every Google Ads campaign that spent in the last {LIVE_CAMPAIGN_DAYS} days.</Typography>
        <Box component="label" sx={{ position: 'relative', display: 'block' }}>
          <SearchIcon aria-hidden="true" sx={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#64748B', pointerEvents: 'none' }} />
          <Box component="span" sx={srOnly}>Search campaigns</Box>
          <Box
            component="input"
            type="text"
            autoFocus
            autoComplete="off"
            placeholder="Search by name, or type a store like UK"
            value={filter}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilter(event.target.value)}
            sx={{ width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: '0.875rem', lineHeight: 1.45, color: '#0F172A', bgcolor: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: '6px', pl: '30px', pr: '10px', py: '7px', '&::placeholder': { color: '#64748B', opacity: 1 }, '&:focus': { outline: 'none', borderColor: '#6366F1', boxShadow: '0 0 0 1px #6366F1' } }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box role="group" aria-label="Show" sx={buttonGroup}>
            <Box component="button" type="button" aria-pressed={!pinned} onClick={() => setPinned(null)} sx={pressable(!pinned)}>All{count(campaigns?.length)}</Box>
            <Box component="button" type="button" aria-pressed={!!pinned} onClick={() => setPinned(new Set(selected))} sx={pressable(!!pinned)}>Selected{count(selected.size)}</Box>
          </Box>
          <Box component="button" type="button" disabled={!selected.size} onClick={() => setSelected(new Set())} sx={{ ...plainButton, ml: 'auto', border: 0, bgcolor: 'transparent', px: '4px', py: '6px', fontSize: '0.8125rem', fontWeight: 600, color: '#475569', '&:hover:not(:disabled)': { color: '#0F172A', textDecoration: 'underline' }, '&:disabled': { opacity: 0.45, cursor: 'default' } }}>Clear all</Box>
        </Box>
        <Box sx={{ height: 400, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
          {loadError ? (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '0.875rem', color: '#0F172A', mb: 1.25 }}>Couldn't load campaigns: {loadError}</Typography>
              <Button size="small" variant="outlined" onClick={load}>Try again</Button>
            </Box>
          ) : !campaigns ? (
            <Box aria-busy="true" sx={{ px: 1.5, py: 1 }}>
              <Box component="span" sx={srOnly}>Loading campaigns…</Box>
              {Array.from({ length: 10 }, (_, index) => <Skeleton key={index} height={30} width={`${50 + (index * 23) % 45}%`} />)}
            </Box>
          ) : !groups.length ? (
            <Typography sx={{ p: 2, fontSize: '0.875rem', color: '#475569' }}>{emptyNote}</Typography>
          ) : groups.map(group => <StoreGroup key={group.store ?? ''} group={group} selected={selected} onToggle={toggle} />)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {saveError && <Typography role="alert" sx={{ mr: 'auto', color: 'error.main', fontSize: '0.8125rem' }}>Couldn't save: {saveError}</Typography>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// One store's campaigns under a sticky heading whose checkbox ticks or clears every row shown.
function StoreGroup({ group, selected, onToggle }: { group: CampaignStoreGroup; selected: Set<string>; onToggle: (ids: string[], on: boolean) => void }) {
  const ids = group.campaigns.map(campaign => campaign.campaignId);
  const ticked = ids.filter(id => selected.has(id)).length;
  const headingId = `campaign-picker-${group.store ?? 'none'}`;
  return (
    <Box role="group" aria-labelledby={headingId}>
      <Box component="label" sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', alignItems: 'center', gap: '6px', px: '6px', bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', cursor: 'pointer' }}>
        <Checkbox size="small" checked={ticked === ids.length} indeterminate={ticked > 0 && ticked < ids.length} onChange={() => onToggle(ids, ticked < ids.length)} sx={{ p: '5px' }} inputProps={{ 'aria-label': `Every ${group.label} campaign shown` }} />
        <Typography id={headingId} component="span" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#475569' }}>{group.label}</Typography>
        <Typography component="span" sx={{ ml: 'auto', pr: '8px', fontSize: '0.75rem', color: '#64748B', fontVariantNumeric: 'tabular-nums' }}>{ticked ? `${ticked} of ${ids.length}` : ids.length}</Typography>
      </Box>
      {group.campaigns.map(campaign => (
        <Box component="label" key={campaign.campaignId} sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', px: '6px', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(100,116,139,0.06)' }, '& + &': { borderTop: '1px solid #F1F5F9' } }}>
          <Checkbox size="small" checked={selected.has(campaign.campaignId)} onChange={event => onToggle([campaign.campaignId], event.target.checked)} sx={{ p: '5px' }} />
          <Box component="span" sx={{ flex: 1, minWidth: 0, py: '6px', fontSize: '0.8125rem', lineHeight: 1.4, color: '#0F172A', overflowWrap: 'anywhere' }}>{campaign.campaignName}</Box>
          {!campaign.live && <Box component="span" sx={{ flexShrink: 0, py: '7px', pr: '8px', fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>{notLiveNote(campaign)}</Box>}
        </Box>
      ))}
    </Box>
  );
}
